/**
 * ContactProfile360Modal — Perfil contacto ampliado (0287 Etapa B–F).
 * Presentación UX: Drawer derecho (expande desde el costado; no modal centrado).
 * Contratos: GET /contacts/:id/360-summary | …timeline | …messages | PUT /contacts/:id (identidad)
 * | PUT …/profile-360 | POST …/picture
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import AddIcon from "@material-ui/icons/Add";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";
import InputAdornment from "@material-ui/core/InputAdornment";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Divider from "@material-ui/core/Divider";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import PersonIcon from "@material-ui/icons/Person";
import ChatIcon from "@material-ui/icons/Chat";
import EditIcon from "@material-ui/icons/Edit";
import WatchLaterIcon from "@material-ui/icons/WatchLater";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import StopIcon from "@material-ui/icons/Stop";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import NotificationsActiveOutlinedIcon from "@material-ui/icons/NotificationsActiveOutlined";
import AlternateEmailOutlinedIcon from "@material-ui/icons/AlternateEmailOutlined";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";

import api, { getProfilePicUrl } from "../../services/api";
import { i18n } from "../../translate/i18n";
import ImageCropModal from "../ImageCropModal";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { generateColor } from "../../helpers/colorGenerator";
import { getInitials } from "../../helpers/getInitials";
import AICaptureBadge from "../BusinessProfileCard/AICaptureBadge";
import { D11, CONTACT360_DRAWER_Z, CONTACT360_SELECT_MENU_Z, PROFILE_PATCH_KEYS, REFERRAL_SOURCE_OPTIONS, HORARIO_HABITUAL_OPTIONS, FRECUENCIA_VISITA_OPTIONS, DEFAULT_METODO_PAGO_OPTIONS } from "./contact360.constants";
import { useStyles } from "./ContactProfile360Styles";
import useContactProfile360 from "./useContactProfile360";
import { stringifyProfileEditableValue, textOrEmpty, isBeautyBusinessProfile, buildPaymentMethodOptions, toDateInputValue, normalizeNotasInternasLogFromProfile, getAiConfidence } from "./contact360.helpers";
import { ProfileField, ProfileDateField, ProfileAiSelect } from "./ProfileFields";
import Contact360CitasTab from "./Contact360CitasTab";
import Contact360HistorialTab from "./Contact360HistorialTab";
import Contact360MensajesTab from "./Contact360MensajesTab";




export default function ContactProfile360Modal({
  open,
  onClose,
  contactId,
  onContactUpdated,
  /** Incrementar desde el padre (p. ej. socket) para forzar refresco desde API. */
  reloadContactSignal = 0
}) {
  const classes = useStyles();
  const {
    addTagsFromInput,
    aiExtractedData,
    appendNotaInterna,
    appointments,
    avatarKey,
    avatarLightboxOpen,
    contact,
    cropModalOpen,
    draft,
    fetchTimelinePage,
    formattedProxima,
    freqLabel,
    handleCropModalClose360,
    handleDraftChange,
    handleProfilePictureCropped,
    handleProfilePicturePick,
    handleSaveProfile,
    historial,
    identity,
    isBeautyProfile,
    kpis,
    legacyMetodoPagoLabel,
    legacyProfesionalLabel,
    legacyReferralLabel,
    legacyServicioLabel,
    loadSummary,
    loading,
    mergeNewTags,
    mountedRef,
    notaInput,
    onboardingConfig,
    paymentCodeSet,
    paymentSelectOptions,
    permissions,
    persistExcludeFromSequences,
    pictureInputRef,
    professionalIdSet,
    professionalSelectOptions,
    referralCodeSet,
    removeTag,
    restricted,
    saving,
    savingExclude,
    selectedCropSource,
    serviceIdSet,
    serviceSelectOptions,
    setAvatarKey,
    setAvatarLightboxOpen,
    setCropModalOpen,
    setDraft,
    setHistorial,
    setIdentity,
    setLoading,
    setNotaInput,
    setOnboardingConfig,
    setSaving,
    setSavingExclude,
    setSelectedCropSource,
    setSummary,
    setTabIndex,
    setTagDraft,
    setUploadingPicture,
    summary,
    tabIndex,
    tagDraft,
    tags,
    uploadingPicture,
    valorLabel,
    whatsappConnName,
  } = useContactProfile360({ open, onClose, contactId, onContactUpdated, reloadContactSignal });

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        className: classes.drawerPaper
      }}
      ModalProps={{
        keepMounted: false,
        disableScrollLock: true,
        style: { zIndex: CONTACT360_DRAWER_Z }
      }}
    >
      <Box component="header" role="banner" className={classes.drawerHeader}>
        <Typography component="div" variant="h6" style={{ flex: 1, fontWeight: 700, color: D11.blue }}>
          Datos del contacto
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <div className={classes.drawerScroll} tabIndex={-1}>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {!loading && summary && (
          <>
            <Box display="flex" alignItems="center" mb={2} flexWrap="wrap" style={{ gap: 16 }}>
              <Box className={classes.headerAvatarWrap}>
                {!cropModalOpen && (
                  <>
                    <Avatar
                      key={avatarKey}
                      className={classes.avatarHeader}
                      style={{
                        backgroundColor: generateColor(identity.number || identity.name || contact?.number || ""),
                        color: "white",
                        fontWeight: "bold",
                        cursor: contact?.profilePicUrl && !String(contact.profilePicUrl).includes("nopicture.png") ? "zoom-in" : "default",
                      }}
                      imgProps={{
                        style: { objectFit: "cover", width: "100%", height: "100%", display: "block" },
                        onError: (e) => {
                          if (e.target) e.target.style.display = "none";
                        },
                        onLoad: (e) => {
                          if (e.target) e.target.style.display = "block";
                        }
                      }}
                      src={
                        contact?.profilePicUrl && !String(contact.profilePicUrl).includes("nopicture.png")
                          ? `${getProfilePicUrl(contact.profilePicUrl)}?t=${avatarKey}`
                          : null
                      }
                      onError={(e) => {
                        if (e.target) e.target.style.display = "none";
                      }}
                      onClick={() => {
                        if (contact?.profilePicUrl && !String(contact.profilePicUrl).includes("nopicture.png")) {
                          setAvatarLightboxOpen(true);
                        }
                      }}
                    >
                      {contact?.profilePicUrl &&
                      String(contact.profilePicUrl) !== "" &&
                      !String(contact.profilePicUrl).includes("nopicture.png")
                        ? null
                        : getInitials(identity.name || "?")}
                    </Avatar>

                    {/* Lightbox foto de perfil */}
                    {avatarLightboxOpen && contact?.profilePicUrl && !String(contact.profilePicUrl).includes("nopicture.png") && (
                      <Dialog
                        open={avatarLightboxOpen}
                        onClose={() => setAvatarLightboxOpen(false)}
                        fullScreen
                        style={{ zIndex: CONTACT360_DRAWER_Z + 100 }}
                        PaperProps={{
                          style: {
                            backgroundColor: "rgba(0,0,0,0.88)",
                            display: "flex",
                            flexDirection: "column",
                            boxShadow: "none",
                          },
                        }}
                      >
                        <Box display="flex" alignItems="center" p={1.5} style={{ flexShrink: 0 }}>
                          <Avatar
                            src={`${getProfilePicUrl(contact.profilePicUrl)}?t=${avatarKey}`}
                            style={{ width: 38, height: 38, marginRight: 12 }}
                          >
                            {getInitials(identity.name || "?")}
                          </Avatar>
                          <Typography variant="subtitle1" style={{ color: "#fff", fontWeight: 500 }}>
                            {identity.name || contact?.name || ""}
                          </Typography>
                          <Box flex={1} />
                          <IconButton onClick={() => setAvatarLightboxOpen(false)} style={{ color: "#fff" }}>
                            <CloseIcon />
                          </IconButton>
                        </Box>
                        <Box
                          flex={1}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          p={3}
                          onClick={() => setAvatarLightboxOpen(false)}
                        >
                          <img
                            src={`${getProfilePicUrl(contact.profilePicUrl)}?t=${avatarKey}`}
                            alt={identity.name || ""}
                            style={{
                              maxWidth: "90%",
                              maxHeight: "85vh",
                              objectFit: "contain",
                              borderRadius: 6,
                              boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Box>
                      </Dialog>
                    )}
                    <IconButton
                      size="small"
                      className={classes.headerAvatarCam}
                      onClick={() => pictureInputRef.current?.click()}
                      disabled={uploadingPicture || saving}
                      aria-label="Cambiar foto de perfil"
                    >
                      {uploadingPicture ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <CameraAltIcon style={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                    <input
                      ref={pictureInputRef}
                      type="file"
                      accept="image/*"
                      className={classes.hiddenFileInput}
                      onChange={handleProfilePicturePick}
                    />
                  </>
                )}
              </Box>
              <Box flex={1} minWidth={160}>
                <Typography component="div" variant="h6">
                  {textOrEmpty(identity.name) || "Sin nombre"}
                </Typography>
                <Typography component="div" variant="body2" style={{ color: D11.placeholder }}>
                  {textOrEmpty(identity.number)}
                </Typography>
                <Typography component="div" variant="body2" color="textSecondary">
                  {textOrEmpty(identity.email) || "Sin correo"}
                </Typography>
                <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                  {(tags.length ? tags : [])
                    .map((t, idx) =>
                      typeof t === "string" && t.trim() ? (
                        <Chip key={`${t}-${idx}`} size="small" label={t.trim()} />
                      ) : null
                    )
                    .filter(Boolean)}
                </Box>
              </Box>
            </Box>

            <Box display="flex" flexWrap="wrap" style={{ gap: 10 }} mb={2}>
              <Paper className={classes.kpiPaper} variant="outlined">
                <Typography variant="caption">Total visitas</Typography>
                <Typography className={classes.kpiValue}>
                  {restricted ? "—" : kpis.totalVisitas != null ? kpis.totalVisitas : "—"}
                </Typography>
              </Paper>
              <Paper className={classes.kpiPaper} variant="outlined">
                <Typography variant="caption">Próx. cita</Typography>
                <Typography className={classes.kpiGreen}>
                  {restricted ? "Sin registrar" : formattedProxima}
                </Typography>
              </Paper>
              <Paper className={classes.kpiPaper} variant="outlined">
                <Typography variant="caption">Frecuencia</Typography>
                <Typography className={classes.kpiOrange}>{restricted ? "—" : freqLabel}</Typography>
              </Paper>
              <Paper className={classes.kpiPaper} variant="outlined">
                <Typography variant="caption">Valor total</Typography>
                <Typography className={classes.kpiGreen}>{restricted ? "—" : valorLabel}</Typography>
              </Paper>
            </Box>

            <Tabs
              value={tabIndex}
              onChange={(_, v) => setTabIndex(v)}
              classes={{ indicator: classes.tabIndicator }}
              textColor="inherit"
              style={{ color: D11.blue, marginBottom: 8 }}
            >
              <Tab label="Perfil" />
              <Tab label="Citas" />
              <Tab label="Historial" />
              <Tab label="Mensajes" />
            </Tabs>

            {tabIndex === 0 && (
              <Box mt={2}>
                <Paper className={classes.bannerIa} elevation={0}>
                  <Typography variant="body2">
                    <strong>Editables (tras «Guardar cambios»):</strong> nombre, WhatsApp, correo y todo el perfil
                    ampliado debajo. La <strong>foto</strong> se guarda al terminar el recorte (no hace falta el botón verde).
                  </Typography>
                </Paper>

                <Typography className={classes.sectionTitle}>IDENTIDAD DEL CONTACTO (columnas principales)</Typography>
                <Typography variant="caption" display="block" style={{ color: D11.placeholder, marginBottom: 8 }}>
                  Estos tres campos persisten en la tabla <strong>Contacts</strong>. Usa «Guardar cambios» para enviarlos
                  junto con el JSON del perfil.
                </Typography>
                <Paper variant="outlined" style={{ padding: "12px 14px", marginBottom: 14, borderRadius: 8 }}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    margin="dense"
                    label="Nombre"
                    value={identity.name}
                    onChange={(e) => setIdentity((prev) => ({ ...prev, name: e.target.value }))}
                    disabled={saving || uploadingPicture}
                    style={{ marginBottom: 10 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    margin="dense"
                    label="WhatsApp (solo números recomendado)"
                    placeholder="51980795334"
                    value={identity.number}
                    onChange={(e) => setIdentity((prev) => ({ ...prev, number: e.target.value }))}
                    disabled={saving || uploadingPicture}
                    style={{ marginBottom: 10 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    margin="dense"
                    label="Email"
                    type="email"
                    value={identity.email}
                    onChange={(e) => setIdentity((prev) => ({ ...prev, email: e.target.value }))}
                    disabled={saving || uploadingPicture}
                  />
                </Paper>
                <ProfileDateField
                  label="Fecha nacimiento"
                  fieldKey="fechaNacimiento"
                  value={draft.fechaNacimiento}
                  onChange={handleDraftChange}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                />
                <ProfileAiSelect
                  label="¿Cómo nos conoció?"
                  fieldKey="comoNosConocio"
                  value={draft.comoNosConocio}
                  onChange={handleDraftChange}
                  options={REFERRAL_SOURCE_OPTIONS}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                  extraLegacyOption={legacyReferralLabel}
                />

                <Typography className={classes.sectionTitle}>TAGS CABECERA (máx. 10)</Typography>
                <Box display="flex" flexWrap="wrap" mb={1} style={{ gap: 6 }}>
                  {(tags.length ? tags : []).map((t) => (
                    <Chip key={t} label={t} onDelete={() => removeTag(t)} size="small" />
                  ))}
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Ej. tinte frecuente — Enter o botón para añadir"
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTagsFromInput();
                    }
                  }}
                  disabled={saving || tags.length >= 10}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button size="small" onClick={addTagsFromInput} disabled={tags.length >= 10 || saving}>
                          Añadir
                        </Button>
                      </InputAdornment>
                    )
                  }}
                />

                <Typography className={classes.sectionTitle}>PREFERENCIAS DEL SERVICIO</Typography>
                {!serviceSelectOptions.length && onboardingConfig && (
                  <Typography variant="caption" display="block" style={{ color: D11.placeholder, marginBottom: 8 }}>
                    No hay servicios en onboarding (Agenda configuración); elige catálogo allí primero.
                  </Typography>
                )}
                <ProfileAiSelect
                  label="Servicio favorito"
                  fieldKey="servicioFavorito"
                  value={draft.servicioFavorito}
                  onChange={handleDraftChange}
                  options={serviceSelectOptions}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                  extraLegacyOption={legacyServicioLabel}
                />
                {!professionalSelectOptions.length && onboardingConfig && (
                  <Typography variant="caption" display="block" style={{ color: D11.placeholder, marginBottom: 8 }}>
                    No hay equipo/profesionales cargados en onboarding.
                  </Typography>
                )}
                <ProfileAiSelect
                  label="Profesional de preferencia"
                  fieldKey="profesional"
                  value={draft.profesional}
                  onChange={handleDraftChange}
                  options={professionalSelectOptions}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                  extraLegacyOption={legacyProfesionalLabel}
                />
                <ProfileAiSelect
                  label="Horario habitual"
                  fieldKey="horarioHabitual"
                  value={draft.horarioHabitual}
                  onChange={handleDraftChange}
                  options={HORARIO_HABITUAL_OPTIONS}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                />
                {isBeautyProfile ? (
                  <>
                    <ProfileField
                      label="Tipo cabello / restricciones"
                      fieldKey="tipoCabello"
                      value={draft.tipoCabello}
                      onChange={handleDraftChange}
                      aiExtractedData={aiExtractedData}
                      disabled={saving}
                    />
                    <ProfileField
                      label="Alergias"
                      fieldKey="alergias"
                      value={draft.alergias}
                      onChange={handleDraftChange}
                      aiExtractedData={aiExtractedData}
                      disabled={saving}
                    />
                  </>
                ) : null}

                <Typography className={classes.sectionTitle}>INFORMACIÓN COMERCIAL</Typography>
                <ProfileAiSelect
                  label="Método de pago habitual"
                  fieldKey="metodoPago"
                  value={draft.metodoPago}
                  onChange={handleDraftChange}
                  options={paymentSelectOptions}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                  extraLegacyOption={legacyMetodoPagoLabel}
                />
                <ProfileAiSelect
                  label="Frecuencia promedio de visita"
                  fieldKey="frecuenciaVisita"
                  value={draft.frecuenciaVisita}
                  onChange={handleDraftChange}
                  options={FRECUENCIA_VISITA_OPTIONS}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                />
                <ProfileAiSelect
                  label="Asesor asignado"
                  fieldKey="asesorAsignado"
                  value={draft.asesorAsignado}
                  onChange={handleDraftChange}
                  options={professionalSelectOptions}
                  aiExtractedData={aiExtractedData}
                  disabled={saving}
                />
                <Box mb={2} mt={1}>
                  <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                    Conexión de origen
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Conexión en sistema: {whatsappConnName || "—"}
                  </Typography>
                  <Typography variant="caption" display="block" style={{ color: D11.placeholder, marginTop: 4 }}>
                    Solo lectura (WhatsApp del contacto). No se duplica en el JSON del perfil al guardar.
                  </Typography>
                </Box>

                <Typography className={classes.sectionTitle}>OBSERVACIONES DEL EQUIPO</Typography>
                <Typography variant="caption" display="block" style={{ color: D11.placeholder, marginBottom: 8 }}>
                  Añade notas con el botón +; se guardan al pulsar «Guardar cambios».
                </Typography>
                <Box mb={1}>
                  {(Array.isArray(draft.notasInternasLog) ? draft.notasInternasLog : []).map((n, idx) => (
                    <Paper
                      key={`${idx}-${n.createdAt || ""}-${n.text?.slice(0, 12)}`}
                      variant="outlined"
                      style={{ padding: "8px 10px", marginBottom: 8, borderRadius: 8 }}
                    >
                      {n.createdAt ? (
                        <Typography variant="caption" style={{ color: D11.placeholder, display: "block" }}>
                          {moment(n.createdAt).format("DD/MM/YYYY HH:mm")}
                        </Typography>
                      ) : null}
                      <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                        {n.text}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
                <Box display="flex" alignItems="flex-start" style={{ gap: 8 }} mb={2}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    multiline
                    rows={2}
                    placeholder="Nueva nota interna (mín. 3 caracteres)…"
                    value={notaInput}
                    onChange={(e) => setNotaInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        appendNotaInterna();
                      }
                    }}
                    disabled={saving}
                  />
                  <IconButton
                    color="primary"
                    aria-label="Añadir nota interna"
                    onClick={appendNotaInterna}
                    disabled={saving}
                    style={{ marginTop: 4 }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>

                <Button
                  variant="contained"
                  className={classes.saveBtn}
                  onClick={handleSaveProfile}
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={22} color="inherit" /> : "Guardar cambios"}
                </Button>
              </Box>
            )}

            {tabIndex === 1 &&
              (restricted ? (
                <Box py={4} px={2}>
                  <Typography variant="body2">{permissions.restrictedMessage}</Typography>
                </Box>
              ) : (
                <Contact360CitasTab
                  appointments={appointments}
                  kpis={kpis}
                  classes={classes}
                  excludeFromSequences={!!draft.excludeFromSequences}
                  savingExclude={savingExclude}
                  onExcludeChange={persistExcludeFromSequences}
                />
              ))}
            {tabIndex === 2 && (
              <Contact360HistorialTab
                classes={classes}
                loading={historial.loading}
                loadingMore={historial.loadingMore}
                events={historial.events}
                iaHints={historial.iaHints}
                total={historial.total}
                timelineRestrictedMsg={historial.timelineRestrictedMsg}
                onLoadMore={() => fetchTimelinePage(false)}
              />
            )}
            {tabIndex === 3 && contactId ? <Contact360MensajesTab classes={classes} contactId={contactId} /> : null}
          </>
        )}
      </div>
    </Drawer>
      <ImageCropModal
        open={cropModalOpen}
        onClose={handleCropModalClose360}
        imageSrc={selectedCropSource}
        onCropComplete={handleProfilePictureCropped}
        aspect={1}
        shape="round"
      />
    </>
  );
}
